import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import useSnackbar from 'shared/hooks/useSnackbar';
import { ACCOUNT_GROUPS } from '@elementary/common-ui/constants/queryKeys';
import * as groupsApi from '../api';
import { Group } from '../types';

export type CreateGroupParams = {
  name: string;
  description?: string | null;
  roles: string[];
  userIds: string[];
};

export type UpdateGroupParams = {
  groupId: string;
  configuration?: {
    name: string;
    description: string | null;
  };
  roles?: string[];
  userIds?: string[];
};

export const useGroups = () => {
  const queryClient = useQueryClient();
  const { openSnackbar } = useSnackbar();

  const {
    data: groups,
    isLoading,
    error,
  } = useQuery({
    queryKey: [ACCOUNT_GROUPS],
    queryFn: groupsApi.fetchGroups,
  });

  const createGroup = async (params: CreateGroupParams) => {
    const createRequest = {
      name: params.name,
      description: params.description,
      roles: params.roles,
    };
    const createdGroup = await groupsApi.createGroup(createRequest);
    if (params.userIds.length > 0) {
      await groupsApi.setGroupUsers(createdGroup.id, {
        user_ids: params.userIds,
      });
    }
    return createdGroup;
  };

  const updateGroup = async (params: UpdateGroupParams) => {
    const pendingPromises: Promise<void | Group>[] = [];
    if (params.configuration !== undefined) {
      pendingPromises.push(
        groupsApi.updateGroup(params.groupId, {
          name: params.configuration.name,
          description: params.configuration.description,
        }),
      );
    }
    if (params.roles !== undefined) {
      pendingPromises.push(
        groupsApi.setGroupRoles(params.groupId, {
          role_ids: params.roles,
        }),
      );
    }
    if (params.userIds !== undefined) {
      pendingPromises.push(
        groupsApi.setGroupUsers(params.groupId, {
          user_ids: params.userIds,
        }),
      );
    }
    await Promise.all(pendingPromises);
  };

  const createGroupMutation = useMutation({
    mutationFn: (params: CreateGroupParams) => createGroup(params),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [ACCOUNT_GROUPS] });
      openSnackbar({
        message: 'Group created successfully',
        variant: 'success',
      });
    },
    onError: () => {
      openSnackbar({
        message: 'Failed to create group',
        variant: 'error',
      });
    },
  });

  const updateGroupMutation = useMutation({
    mutationFn: (params: UpdateGroupParams) => updateGroup(params),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [ACCOUNT_GROUPS] });
      openSnackbar({
        message: 'Group updated successfully',
        variant: 'success',
      });
    },
    onError: () => {
      openSnackbar({
        message: 'Failed to update group',
        variant: 'error',
      });
    },
  });

  const deleteGroupMutation = useMutation({
    mutationFn: (groupId: string) => groupsApi.deleteGroup(groupId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [ACCOUNT_GROUPS] });
      openSnackbar({
        message: 'Group deleted successfully',
        variant: 'success',
      });
    },
    onError: () => {
      openSnackbar({
        message: 'Failed to delete group',
        variant: 'error',
      });
    },
  });

  return {
    groups,
    isLoading,
    error,
    createGroup: createGroupMutation.mutate,
    createGroupAsync: createGroupMutation.mutateAsync,
    isCreating: createGroupMutation.isLoading,
    updateGroup: updateGroupMutation.mutate,
    updateGroupAsync: updateGroupMutation.mutateAsync,
    isUpdating: updateGroupMutation.isLoading,
    deleteGroup: deleteGroupMutation.mutate,
    deleteGroupAsync: deleteGroupMutation.mutateAsync,
    isDeleting: deleteGroupMutation.isLoading,
  };
};
